'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

// lib
import { accessibilityLabel } from '../../_support'
import { MaskedInput, MaskedInputProps } from '../inputs'
import {
  FieldContainer,
  FieldContainerProps,
  FieldErrorMessage,
  FieldErrorNotification,
  FieldIcon,
  FieldLabel,
  FieldPlaceholder,
  useField,
  UseFieldOptions
} from '../field'
import { colors, fonts } from '@theme'

/* -----------------------------------------------------------------------------
 * MaskedField
 * -------------------------------------------------------------------------- */

type FontKeys = keyof typeof fonts

type ColorsKeys = keyof typeof colors

export type MaskedFieldProps = FieldContainerProps &
  UseFieldOptions &
  MaskedInputProps & {
    label: string
    placeholder?: string
    nativeMask: string
    showErrorMessage?: boolean
    webMask: Array<RegExp | string>
    fieldIcon?: React.ReactNode
    onIconPress?: () => void
    // font from theme
    placeholderFont?: FontKeys | string
    labelFont?: FontKeys | string
    labelTextColor?: ColorsKeys | string
  }

const MaskedField = React.forwardRef(
  (
    {
      children,
      label,
      name,
      placeholder = '',
      showErrorMessage = true,
      fieldIcon,
      onIconPress,
      placeholderFont,
      labelFont,
      labelTextColor,
      ...props
    }: MaskedFieldProps,
    ref: React.MutableRefObject<HTMLInputElement>
  ) => {
    const {
      fieldState,
      fieldError,
      fieldAutofilled,
      inputProps,
      containerProps
    } = useField({
      name,
      ...props
    })

    return (
      <FieldContainer {...containerProps}>
        <MaskedInput
          ref={ref}
          {...accessibilityLabel(`${name} Input`)}
          {...inputProps}
        >
          <FieldLabel
            fieldState={fieldState}
            error={!!fieldError}
            font={labelFont as any}
            textColor={labelTextColor as any}
          >
            {label}
          </FieldLabel>
          {fieldIcon && !fieldError && (
            <FieldIcon icon={fieldIcon} onPress={onIconPress} />
          )}
          {fieldError && <FieldErrorNotification />}
          {!fieldAutofilled &&
            fieldState === 'focused' &&
            inputProps.value === '' && (
              <FieldPlaceholder font={placeholderFont as any}>
                {placeholder}
              </FieldPlaceholder>
            )}
        </MaskedInput>
        {fieldError && showErrorMessage && (
          <FieldErrorMessage>{fieldError}</FieldErrorMessage>
        )}
      </FieldContainer>
    )
  }
)

export default MaskedField
